import * as PIXI from "pixi.js"
import { ID, Idea } from "../model/types"
import {
  clearIdeaTextFromGraphic,
  renderIdeaTextToGraphic,
  setIdeaTextLocation,
} from "./idea_text_rendering"
import { defaultIdeaColourResolver, type IdeaColourResolver } from "./idea_colour"

export type IdeaTextRenderState = {
  // Pixi text objects by idea id, so later updates can hide, move, clear, or rerender one idea.
  text_by_idea_id: Map<ID, PIXI.Text>
  // Ideas rendered during the initial onscreen pass; lazy rendering skips them to avoid duplicates.
  initially_rendered_idea_ids: Set<ID>
  // Index into the original idea list for the next lazy-render batch.
  next_lazy_idea_index: number
}

export default class IdeaTextRenderer {
  private text_render_state: IdeaTextRenderState
  private ideas_graphic: () => PIXI.Graphics | null
  private idea_colour_resolver: () => IdeaColourResolver
  private request_render: () => void
  private lazy_render_timer: number | null = null
  private texts_visible = true

  constructor(
    ideas_graphic: () => PIXI.Graphics | null,
    idea_colour_resolver: () => IdeaColourResolver = () => defaultIdeaColourResolver,
    request_render: () => void = () => {},
  ) {
    this.ideas_graphic = ideas_graphic
    this.idea_colour_resolver = idea_colour_resolver
    this.request_render = request_render
    this.text_render_state = {
      text_by_idea_id: new Map(),
      initially_rendered_idea_ids: new Set(),
      next_lazy_idea_index: 0,
    }
  }

  render_idea_text(idea: Idea): void {
    const ideas_graphic = this.ideas_graphic()
    if (ideas_graphic != null) {
      const text = renderIdeaTextToGraphic(
        ideas_graphic,
        idea,
        this.text_render_state.text_by_idea_id,
        0,
        0,
        this.idea_colour_resolver(),
      )
      if (text) text.renderable = this.texts_visible
    }
  }

  clear_idea_text(idea: Idea): void {
    const ideas_graphic = this.ideas_graphic()
    if (ideas_graphic != null)
      clearIdeaTextFromGraphic(ideas_graphic, idea, this.text_render_state.text_by_idea_id)
  }

  rerender_idea_text(idea: Idea): void {
    const ideas_graphic = this.ideas_graphic()
    if (ideas_graphic == null) return

    clearIdeaTextFromGraphic(ideas_graphic, idea, this.text_render_state.text_by_idea_id)
    const text = renderIdeaTextToGraphic(
      ideas_graphic,
      idea,
      this.text_render_state.text_by_idea_id,
      0,
      0,
      this.idea_colour_resolver(),
    )
    if (text) text.renderable = this.texts_visible
  }

  rerender_rendered_idea_texts(ideas_by_id: { get(id: ID): Idea | undefined }): void {
    Array.from(this.text_render_state.text_by_idea_id.keys()).forEach((id) => {
      const idea = ideas_by_id.get(id)
      if (idea) this.rerender_idea_text(idea)
    })
  }

  hide_idea_texts(idea_ids: ID[]): void {
    idea_ids.forEach((id) => {
      const text = this.text_render_state.text_by_idea_id.get(id)
      if (text) text.renderable = false
    })
  }

  show_idea_texts(idea_ids: ID[]): void {
    idea_ids.forEach((id) => {
      const text = this.text_render_state.text_by_idea_id.get(id)
      if (text) text.renderable = this.texts_visible
    })
  }

  set_texts_visible(is_visible: boolean): void {
    this.texts_visible = is_visible
    this.text_render_state.text_by_idea_id.forEach((text) => {
      text.renderable = is_visible
    })
  }

  position_idea_text(idea: Idea): void {
    const text = this.text_render_state.text_by_idea_id.get(idea.id)
    if (text) setIdeaTextLocation(idea, text, 0, 0)
  }

  render_onscreen_idea_texts(ideas: Idea[], is_idea_onscreen: (idea: Idea) => boolean): void {
    ideas.forEach((idea) => {
      if (is_idea_onscreen(idea)) {
        this.render_idea_text(idea)
        this.text_render_state.initially_rendered_idea_ids.add(idea.id)
      }
    })
  }

  lazy_render_idea_texts(ideas: Idea[]): void {
    const batch_size = 50
    this.lazy_render_timer = window.setTimeout(() => {
      this.lazy_render_timer = null
      if (this.text_render_state.next_lazy_idea_index < ideas.length) {
        const stop = Math.min(
          ideas.length,
          this.text_render_state.next_lazy_idea_index + batch_size,
        )
        for (let i = this.text_render_state.next_lazy_idea_index; i < stop; i++) {
          if (!this.text_render_state.initially_rendered_idea_ids.has(ideas[i].id))
            this.render_idea_text(ideas[i])
          this.text_render_state.next_lazy_idea_index++
        }
        this.request_render()
      }
      if (this.text_render_state.next_lazy_idea_index < ideas.length)
        this.lazy_render_timer = window.setTimeout(() => this.lazy_render_idea_texts(ideas), 10)
    }, 100)
  }

  destroy(): void {
    if (this.lazy_render_timer != null) window.clearTimeout(this.lazy_render_timer)
    this.lazy_render_timer = null
  }
}
